import { LanguagesEnum } from '@/api/enums';
import {
  ContentItemType,
  CourseContent,
  CourseContentType,
  CourseFormState,
  ModifiedCourseTranslationsType,
  ModifiedResource,
  ResourceType,
  SlideDeck,
} from '@/client/courses';
import {
  LoadingStateType,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import { client } from '@/client';
import { ModifiedQuiz, QuizType } from '@/client/quizzes/types';
import { User } from '@/client/users';
import { SystemRoles } from '@/client/users/types/roles.type';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import {
  canPreviewContent,
  FeedbackIcon,
  getCourseContentTitle,
  PdfIcon,
  QuizIcon,
  ScormIcon,
  SlideDeckIcon,
  VideoIcon,
} from '@/utils/helpers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TFunction } from 'i18next';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import React, {
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { ContentItem } from './ContentItem';

function resolveCourseTitleForAutoNaming(
  courseTranslations: ModifiedCourseTranslationsType[],
  defaultLanguage: LanguagesEnum | undefined,
  courseLanguages: LanguagesEnum[] | undefined,
  uiLanguage: string | undefined,
): string {
  const dict = courseTranslations as any;
  if (uiLanguage) {
    const uiTitle = dict[uiLanguage]?.name;
    if (uiTitle) return uiTitle;
  }
  if (defaultLanguage) {
    const defaultTitle = dict[defaultLanguage]?.name;
    if (defaultTitle) return defaultTitle;
  }
  const enTitle = dict[LanguagesEnum.EN]?.name;
  if (enTitle) return enTitle;
  for (const lang of courseLanguages || []) {
    const title = dict[lang]?.name;
    if (title) return title;
  }
  for (const lang of Object.keys(dict)) {
    const title = dict[lang]?.name;
    if (title) return title;
  }
  return '';
}

function truncateTitle(title: string): string {
  const words = title.trim().split(/\s+/);
  if (words.length <= 3) return title;
  return words.slice(0, 3).join(' ') + '…';
}

function getAutoNameTypeLabel(type: ContentItemType, t: TFunction): string {
  switch (type) {
    case ContentItemType.VIDEO:
      return t('content.type.video');
    case ContentItemType.PDF:
      return t('content.type.pdf');
    case ContentItemType.QUIZ:
      return t('content.type.quiz');
    case ContentItemType.SCORM:
      return t('content.type.scorm');
    case ContentItemType.PRESENTATION:
      return t('content.type.slidedeck');
    default:
      return '';
  }
}

function resolveContentItemType(item: CourseContent): ContentItemType | null {
  if (item.presentation) return ContentItemType.PRESENTATION;
  if (item.quiz?.type === QuizType.TRADITIONAL) return ContentItemType.QUIZ;
  if (item.resource?.type === ResourceType.VIDEO) return ContentItemType.VIDEO;
  if (item.resource?.type === ResourceType.PDF) return ContentItemType.PDF;
  if (item.resource?.type === ResourceType.SCORM) return ContentItemType.SCORM;
  return null;
}

function getContentSubTitle(item: CourseContent): string {
  return getCourseContentTitle(item);
}

function getNextSuffix(
  type: ContentItemType,
  courseContents: CourseContent[],
): number | null {
  const sameType = courseContents.filter(
    (c) => resolveContentItemType(c) === type,
  );
  if (sameType.length === 0) return null;

  let maxSuffix = 1;
  for (const item of sameType) {
    if (!item.isAutoNamed) continue;
    const title = getContentSubTitle(item);
    const match = title.match(/\s(\d+)$/);
    if (match) {
      maxSuffix = Math.max(maxSuffix, parseInt(match[1], 10));
    }
  }

  return Math.max(sameType.length + 1, maxSuffix + 1);
}

function generateAutoName(
  courseTranslations: ModifiedCourseTranslationsType[],
  defaultLanguage: LanguagesEnum | undefined,
  courseLanguages: LanguagesEnum[] | undefined,
  adminLanguage: string | undefined,
  type: ContentItemType,
  courseContents: CourseContent[],
  t: TFunction,
): string {
  const rawTitle = resolveCourseTitleForAutoNaming(
    courseTranslations,
    defaultLanguage,
    courseLanguages,
    adminLanguage,
  );

  if (!rawTitle) {
    let fallback: string;
    switch (type) {
      case ContentItemType.VIDEO:
        fallback = t('content.new.video');
        break;
      case ContentItemType.PDF:
        fallback = t('content.new.pdf');
        break;
      case ContentItemType.QUIZ:
        fallback = t('content.new.quiz');
        break;
      case ContentItemType.SCORM:
        fallback = t('content.new.scorm');
        break;
      case ContentItemType.PRESENTATION:
        fallback = t('content.new.slidedeck');
        break;
      default:
        return '';
    }
    const suffix = getNextSuffix(type, courseContents);
    return suffix ? `${fallback} ${suffix}` : fallback;
  }

  const truncated = truncateTitle(rawTitle);
  const typeLabel = getAutoNameTypeLabel(type, t);
  const suffix = getNextSuffix(type, courseContents);

  return suffix
    ? `${truncated} – ${typeLabel} ${suffix}`
    : `${truncated} – ${typeLabel}`;
}

const StyledFlexContainer = styled(FlexContainer)`
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--beige-main);
  margin-top: var(--big-padding);
  padding: var(--default-padding);

  h5 {
    color: var(--black-main);
    font-size: 18px;
    line-height: 140%;
    margin: 0;
  }

  .secondary-text {
    color: var(--gray-darker);
    font-size: var(--small-font-size);
    font-weight: 400;
    line-height: var(--small-line-height);
    margin: 0;
  }

  .preview {
    margin-top: 20px;
    margin-bottom: var(--small-padding);
  }

  .p-menuitem-link {
    svg {
      margin-right: var(--small-padding);
    }
  }
`;

const SortableItemWrapper = styled.div`
  position: relative;

  &:active {
    cursor: grabbing;
  }
`;

const DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

interface SortableItemProps {
  id: string | number;
  index: number;
  content: CourseContent;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
  values: CourseFormState;
  disabled?: boolean;
  state?: LoadingStateType;
  isAnyDragging: boolean;
  currentUser: User | null;
}

const SortableItem = ({
  id,
  index,
  setFieldValue,
  values,
  disabled,
  state,
  isAnyDragging,
  setIsSubmitDisabled,
  content,
  currentUser,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: content?.quiz?.type === QuizType.FEEDBACK, // Disable dragging for FEEDBACK items
  });

  const hideFeedback =
    values?.cloneMetadata?.isClone &&
    currentUser?.role?.code !== SystemRoles.DEVELOPER &&
    currentUser?.role?.code !== SystemRoles.SUPER_ADMIN;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    width: '100%',
  };

  if (content?.quiz?.type === QuizType.FEEDBACK && hideFeedback) {
    return null;
  }

  return (
    <SortableItemWrapper ref={setNodeRef} style={style}>
      {!disabled && content?.quiz?.type !== QuizType.FEEDBACK && (
        <DragHandle {...attributes} {...listeners} />
      )}
      <ContentItem
        index={index}
        setFieldValue={setFieldValue}
        setIsSubmitDisabled={setIsSubmitDisabled}
        values={values}
        isDisabled={disabled}
        state={state}
        isSingle={values?.courseContents?.length === 1}
        isDragging={isAnyDragging}
        isFirst={index === 0}
        isLast={
          hideFeedback &&
          values?.courseContents?.some(
            (content: CourseContent) =>
              content?.quiz?.type === QuizType.FEEDBACK,
          )
            ? index === values.courseContents.length - 2
            : index === values.courseContents.length - 1
        }
      />
    </SortableItemWrapper>
  );
};

type ContentTimelineProps = {
  initialValues: CourseFormState;
  values: CourseFormState;
  state?: LoadingStateType;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
  pageLeaveHandler: (values: CourseFormState, url: string) => void;
  disabled?: boolean;
};

export const ContentTimeline: React.FC<ContentTimelineProps> = ({
  initialValues,
  values,
  state,
  setFieldValue,
  setIsSubmitDisabled,
  pageLeaveHandler,
  disabled,
}) => {
  const { t } = useTranslation();
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const currentUser = useAppSelector(selectCurrentUser);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const regularContents =
    values?.courseContents?.filter(
      (content: CourseContent) => content.quiz?.type !== QuizType.FEEDBACK,
    ) || [];
  const feedbackContents =
    values?.courseContents?.filter(
      (content: CourseContent) => content.quiz?.type === QuizType.FEEDBACK,
    ) || [];
  const sortedContents = [...regularContents, ...feedbackContents];

  const contentMenu = useRef<Menu>(null);
  const contentMenuItems: MenuItem[] = useMemo(() => {
    const items = [
      {
        label: t('content.add.video'),
        icon: <VideoIcon />,
        command: () => addContentItem(ContentItemType.VIDEO),
      },
      {
        label: t('content.add.pdf'),
        icon: <PdfIcon />,
        command: () => addContentItem(ContentItemType.PDF),
      },
      {
        label: t('content.add.slidedeck'),
        icon: <SlideDeckIcon />,
        command: () => addContentItem(ContentItemType.PRESENTATION),
      },
      {
        label: t('content.add.quiz'),
        icon: <QuizIcon />,
        command: () => addContentItem(ContentItemType.QUIZ),
        disabled: values?.courseContents?.some(
          (content: CourseContent) =>
            content?.resource?.type === ResourceType.SCORM ||
            content?.quiz?.type === QuizType.TRADITIONAL,
        ),
      },
      {
        label: t('content.add.scorm'),
        icon: <ScormIcon />,
        command: () => addContentItem(ContentItemType.SCORM),
        disabled: values?.courseContents?.some(
          (content: CourseContent) => content.type === CourseContentType.QUIZ,
        ),
      },
    ];

    if (
      currentUser?.role?.code === SystemRoles.DEVELOPER ||
      currentUser?.role?.code === SystemRoles.SUPER_ADMIN
    ) {
      items.push({
        label: t('content.add.feedback'),
        icon: <FeedbackIcon />,
        command: () => addContentItem(ContentItemType.FEEDBACK),
        disabled: values?.courseContents?.some(
          (content: CourseContent) => content?.quiz?.type === QuizType.FEEDBACK,
        ),
      });
    }

    return items;
  }, [currentUser?.role?.code, t, values?.courseContents]);

  const showPreview = useMemo(
    () => canPreviewContent(values, currentUser),
    [values, currentUser],
  );

  const toggleItemActionMenu = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (!contentMenu.current) {
      return;
    }

    contentMenu.current.toggle(event);
  };

  const addContentItem = async (type: ContentItemType) => {
    const currentValues = valuesRef.current;
    const isFeedback = type === ContentItemType.FEEDBACK;
    const autoName = !isFeedback
      ? generateAutoName(
          currentValues.courseTranslations,
          currentValues.defaultLanguage,
          currentValues.courseLanguages,
          currentUser?.language,
          type,
          currentValues.courseContents,
          t,
        )
      : '';

    const item = {
      id: `temp-${v4()}`,
      type,
      translations: {},
      resource: undefined,
      isAutoNamed: !isFeedback,
      position:
        type === ContentItemType.FEEDBACK
          ? currentValues.courseContents.length
          : currentValues.courseContents.filter(
              (content) => content.type !== ContentItemType.FEEDBACK,
            ).length,
    } as CourseContent;

    switch (type) {
      case ContentItemType.PRESENTATION:
        item.presentation = {
          title: autoName,
          translations: [],
        } as SlideDeck;
        break;
      case ContentItemType.QUIZ:
        item.quiz = {
          type: QuizType.TRADITIONAL,
          translations: {},
          questions: [],
          title: autoName,
          account: '',
        } as ModifiedQuiz;
        break;
      default:
        item.resource = {
          type: item.type as unknown as ResourceType,
          title: autoName,
          translations: {},
        } as ModifiedResource;
        break;
    }

    if (type === ContentItemType.FEEDBACK) {
      const feedbackQuizzes = await client.quizzes.getFeedbackQuizzes();

      item.type = ContentItemType.QUIZ;
      item.quiz = feedbackQuizzes?.result[0];

      setFieldValue('courseContents', [...currentValues.courseContents, item]);
    } else {
      const regularContents = currentValues.courseContents.filter(
        (content) => content.quiz?.type !== QuizType.FEEDBACK,
      );
      const feedbackContents = currentValues.courseContents.filter(
        (content) => content.quiz?.type === QuizType.FEEDBACK,
      );
      setFieldValue('courseContents', [
        ...regularContents,
        item,
        ...feedbackContents,
      ]);
    }
  };

  // Ensure that all files have unique IDs, this is needed for DnD
  useEffect(() => {
    if (values?.courseContents?.length > 0) {
      const contentsWithIds = values.courseContents.map(
        (content: CourseContent) => ({
          ...content,
          id: content.id || `temp-${v4()}`,
        }),
      );

      if (
        JSON.stringify(contentsWithIds) !==
        JSON.stringify(values.courseContents)
      ) {
        setFieldValue('courseContents', contentsWithIds);
      }
    }
  }, [values?.courseContents, setFieldValue]);

  const resolvedCourseTitle = useMemo(
    () =>
      resolveCourseTitleForAutoNaming(
        values.courseTranslations,
        values.defaultLanguage,
        values.courseLanguages,
        currentUser?.language,
      ),
    [
      values.courseTranslations,
      values.defaultLanguage,
      values.courseLanguages,
      currentUser?.language,
    ],
  );

  useEffect(() => {
    if (!resolvedCourseTitle) return;
    const truncated = truncateTitle(resolvedCourseTitle);
    const currentContents = valuesRef.current.courseContents;

    currentContents.forEach((item, idx) => {
      if (!item.isAutoNamed) return;
      if (item.quiz?.type === QuizType.FEEDBACK) return;

      const itemType = resolveContentItemType(item);
      if (!itemType) return;

      const typeLabel = getAutoNameTypeLabel(itemType, t);
      const currentTitle = getContentSubTitle(item);
      const suffixMatch = currentTitle.match(/\s(\d+)$/);
      const suffix = suffixMatch ? ` ${suffixMatch[1]}` : '';
      const newTitle = `${truncated} – ${typeLabel}${suffix}`;

      if (currentTitle !== newTitle) {
        if (item.presentation) {
          setFieldValue(`courseContents.${idx}.presentation.title`, newTitle);
        } else if (item.quiz) {
          setFieldValue(`courseContents.${idx}.quiz.title`, newTitle);
        } else if (item.resource) {
          setFieldValue(`courseContents.${idx}.resource.title`, newTitle);
        }
      }
    });
  }, [resolvedCourseTitle]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragStart = () => {
    setIsAnyDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const activeContent = values?.courseContents.find(
        (content: CourseContent) => content?.id === active.id,
      );
      const overContent = values?.courseContents.find(
        (content: CourseContent) => content?.id === over?.id,
      );

      // Prevent dragging if trying to move a FEEDBACK item or move an item after a FEEDBACK item
      if (
        activeContent?.quiz?.type === QuizType.FEEDBACK ||
        overContent?.quiz?.type === QuizType.FEEDBACK
      ) {
        setIsAnyDragging(false);
        return;
      }

      const oldIndex = values?.courseContents.findIndex(
        (content: CourseContent) => content?.id === active.id,
      );
      const newIndex = values?.courseContents.findIndex(
        (content: CourseContent) => content?.id === over?.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const regularContents = values?.courseContents.filter(
          (content: CourseContent) => content?.quiz?.type !== QuizType.FEEDBACK,
        );
        const feedbackContents = values?.courseContents.filter(
          (content: CourseContent) => content?.quiz?.type === QuizType.FEEDBACK,
        );

        // Reorder only the regular contents
        const reorderedRegularContents = arrayMove(
          regularContents,
          oldIndex,
          newIndex,
        );

        const updatedRegularContents = reorderedRegularContents.map(
          (content: CourseContent, index: number) => ({
            ...content,
            position: index,
          }),
        );

        const updatedContents = [
          ...updatedRegularContents,
          ...feedbackContents,
        ];

        setFieldValue('courseContents', updatedContents);
      }
    }

    setIsAnyDragging(false);
  };

  return (
    <StyledFlexContainer direction="column" className="content-timeline">
      <FlexContainer>
        {!disabled && !initialValues.retired && (
          <FlexContainer justify="space-between">
            <div>
              <h5>{t('content.timeline')}</h5>
              <p className="secondary-text">
                {t('content.drag.items.to.reorder')}
              </p>
            </div>
            <Menu
              id="overlay_menu"
              model={contentMenuItems}
              popup
              ref={contentMenu}
            />
            <AppButton
              severity="secondary"
              label={t('content.add')}
              state={state}
              aria-controls="overlay_menu"
              onClick={toggleItemActionMenu}
            />
          </FlexContainer>
        )}
      </FlexContainer>

      <FlexContainer
        direction="column"
        gap={14}
        style={{
          marginTop: 'var(--default-padding)',
          display: sortedContents?.length ? 'flex' : 'none',
          paddingBottom: showPreview ? 0 : 'var(--default-padding)',
        }}
      >
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedContents.map(
              (content: CourseContent) => content?.id || `temp-${v4()}`,
            )}
            strategy={verticalListSortingStrategy}
            disabled={disabled}
          >
            {sortedContents.map((content: CourseContent, index: number) => (
              <SortableItem
                key={content?.id || `temp-${v4()}`}
                id={content?.id || `temp-${v4()}`}
                index={index}
                content={content}
                values={values}
                disabled={disabled}
                state={state}
                isAnyDragging={isAnyDragging}
                setFieldValue={setFieldValue}
                setIsSubmitDisabled={setIsSubmitDisabled}
                currentUser={currentUser}
              />
            ))}
          </SortableContext>
        </DndContext>
        {showPreview && (
          <>
            <div className="preview ml-auto">
              <AppButton
                label={t('content.preview')}
                isDisabled={!values.id}
                onClick={() => {
                  if (!values.id) return;

                  const sanitizedContents = values?.courseContents.map(
                    // Strip the temporary IDs from the course files
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ({ id, ...rest }: CourseContent) => rest,
                  );

                  pageLeaveHandler(
                    {
                      ...values,
                      courseContents: sanitizedContents,
                    },
                    RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](
                      values.id,
                    ),
                  );
                }}
              />
            </div>
            {!values.id && (
              <Tooltip
                position="top"
                target=".preview"
                content={t('content.save.before.preview')}
              />
            )}
          </>
        )}
      </FlexContainer>
    </StyledFlexContainer>
  );
};
